import { Component, inject, OnInit } from '@angular/core';
import { CMSLanguageService, TableContext } from '@markant-platform/shared/data-access';
import { catchError, combineLatest, distinctUntilChanged, map, Observable, of, switchMap, tap } from 'rxjs';
import { Table } from '@markant-platform/shared/table/feature';
import {
  ENTRY_ID,
  SortDirections,
  TableDraft,
  TableDraftBuilder,
  TableParamConfig,
} from '@markant-platform/shared/table/model';
import { HttpErrorResponse } from '@angular/common/http';
import { CartridgeDto } from '@markant-platform/shared/cartridge/model';
import { deepEqual } from '@markant-platform/shared/util-helpers';
import { CartridgeOverviewInfo } from '@markant-platform/shared/organization/model';
import { TABLE_HEADER_AND_CELLS } from './configuration/cartridges-overview.config';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BackofficeCartridgeService } from '@markant-platform/backoffice/data-access';
import { CartridgesFacade } from '@markant-platform/cartridge/data-access';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';

@Component({
  selector: 'markant-platform-backoffice-cartridges-overview',
  templateUrl: './cartridges-overview.component.html',
  styleUrls: ['./cartridges-overview.component.scss'],
})
export class CartridgesOverviewComponent extends Table implements OnInit {
  private readonly backofficeCartridgeService = inject(BackofficeCartridgeService);
  private readonly cartridgesFacade = inject(CartridgesFacade);
  private readonly snackbarService = inject(SnackbarService);
  private readonly cmsLanguageService = inject(CMSLanguageService);

  override tableDraft: TableDraft = new TableDraftBuilder(TABLE_HEADER_AND_CELLS)
    .sortableColumns(['name'])
    .rowLink(['./', ENTRY_ID, 'members'])
    .build();

  constructor() {
    super(TableContext.CARTRIDGE_OVERVIEW);
  }

  override async ngOnInit(): Promise<void> {
    super.ngOnInit();

    this.cmsLanguageService.activeCMSLanguage$
      .pipe(
        switchMap(() => this.tableParams$.pipe(distinctUntilChanged(deepEqual))),
        tap(({ sort }: TableParamConfig) => {
          this.tableDraft.sortStartColumnId = sort?.sortField;
          this.tableDraft.sortStartDirection = sort?.sortOrder;
          this.isLoading$.next(true);
        }),
        switchMap(tableParams => this.getAllCartridgeVariants(tableParams)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: result => {
          this.totalCount = result.length;
          this.reload(result);
        },
      });
  }

  private getAllCartridgeVariants(tableParams: TableParamConfig): Observable<CartridgeOverviewInfo[]> {
    return this.backofficeCartridgeService.getAllCartridgeVariants().pipe(
      catchError((error: HttpErrorResponse) => {
        this.snackbarService.showErrorSnackbar(error.message);
        return of([]);
      }),
      map(cartridges => {
        if (tableParams.searchQuery) {
          return cartridges.filter(cartridge =>
            cartridge.variantName?.toLowerCase().includes(tableParams.searchQuery.toLowerCase())
          );
        }
        return cartridges;
      }),
      map(cartridges => this.returnSortedCartridges(cartridges, tableParams.sort?.sortOrder ?? 'desc')),
      switchMap(data => this.mapCartridgeServiceCartridges(data))
    );
  }

  private readonly mapCartridgeServiceCartridges = (
    cartridges: CartridgeDto[]
  ): Observable<CartridgeOverviewInfo[]> => {
    if (!cartridges.length) {
      return of([]);
    }

    const mappedData = cartridges.map(cartridge =>
      this.cartridgesFacade.cartridgeVariantById$(cartridge.variantId).pipe(
        map(variant => {
          return {
            id: cartridge.variantId,
            name: cartridge.variantName,
            description: cartridge.variantDescription,
            showNotUiBookableChip: !variant?.allowUiBooking ? 'TRUE' : undefined,
          } as CartridgeOverviewInfo;
        })
      )
    );
    return combineLatest(mappedData);
  };

  private returnSortedCartridges(cartridges: CartridgeDto[], sortDirection: SortDirections): CartridgeDto[] {
    return cartridges.sort((a, b) => {
      const aValue = a.variantName ?? '';
      const bValue = b.variantName ?? '';
      return sortDirection === 'desc' ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue);
    });
  }
}
